import { retry } from './retryPolicy';
import { bookClassSlot, AUTH_EXPIRED_ERROR_CODE } from '@/services/edgeFunctionWrappers/bookingsRpcWrapper';

const NON_RETRYABLE_BOOKING_ERROR_CODES = new Set([
  'NO_VALID_SUBSCRIPTION_FOR_DATE',
  'NO_SESSIONS_AVAILABLE',
  // Refus d'entitlement : le calendrier du cours n'est pas ouvert par
  // l'abonnement. C'est un verdict, pas un aléa — le rejouer produit trois
  // fois le même refus, en ajoutant 3 s d'attente et deux invocations edge
  // inutiles par participant avant d'afficher le message.
  'CALENDAR_NOT_ALLOWED',
  // Session expirée : `invokeWithRetry` a déjà tenté un refresh puis déconnecté
  // et redirigé. Réessayer ne fait qu'ajouter ~9 s d'attente avant l'échec.
  AUTH_EXPIRED_ERROR_CODE
]);

function isRetryableBookingError(error: unknown): boolean {
  const code = (error as { code?: string })?.code;
  const message = (error as { message?: string })?.message || String(error);
  return !Array.from(NON_RETRYABLE_BOOKING_ERROR_CODES).some((businessCode) => (
    code === businessCode || message.includes(businessCode)
  ));
}

/**
 * Parameters for booking a class slot
 */
export interface BookClassParams {
  classId: string;
  userId: string;
  date: string;
  time: string;
  duration: number;
  gymId: string;
}

/**
 * Book a class slot with automatic retry on network failure
 * 
 * Retries up to 3 times with 1.5 second delays between attempts.
 * This helps handle temporary network issues without losing the booking action.
 * 
 * @param params - Booking parameters
 * @returns RPC response data
 * @throws Error if all retry attempts fail
 */
export async function bookClassWithRetry(params: BookClassParams) {
  return retry(
    async () => {
      return await bookClassSlot({
        classId: params.classId,
        classDate: params.date,
        time: params.time,
        duration: params.duration,
        userId: params.userId
      }, params.gymId);
    },
    3,    // 3 retry attempts
    1500,  // 1.5 seconds between attempts
    isRetryableBookingError
  );
}
